import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Ban, Flag } from "lucide-react";
import { useBlockedUsers } from "../contexts/BlockedUsersContext";
import { useNotification } from "../contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
import ConfirmationPopup from "../ConfirmationPopup";
import "./styles/BlockUserButton.css";

const MIN_REASON_LENGTH = 30;

interface BlockUserButtonProps {
  userId: string;
  userName: string;
  currentUserId: string | null;
}

type ActiveModal = "block" | "report" | null;

const BlockUserButton: React.FC<BlockUserButtonProps> = ({
  userId,
  userName,
  currentUserId,
}) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [reportReason, setReportReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const { blockUser, isBlocked } = useBlockedUsers();
  const { triggerNotification } = useNotification();

  // Reset report form when modal closes
  useEffect(() => {
    if (activeModal !== "report") {
      setReportReason("");
      setReasonError("");
    }
  }, [activeModal]);

  // Never show for own profile or already-blocked users
  if (!userId || !currentUserId || userId === currentUserId) return null;
  if (isBlocked(userId)) return null;

  const handleBlockConfirm = () => {
    blockUser(userId);
    setActiveModal(null);
    triggerNotification(
      `${userName} has been blocked. Their content is now hidden from you.`,
      NotificationType.SUCCESS
    );
  };

  const handleReportSubmit = () => {
    if (reportReason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(`Please provide at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    setActiveModal(null);
    triggerNotification(
      "Report submitted. Thank you for helping keep Nine safe.",
      NotificationType.SUCCESS
    );
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReportReason(e.target.value);
    if (reasonError && e.target.value.trim().length >= MIN_REASON_LENGTH) {
      setReasonError("");
    }
  };

  const charsLeft = Math.max(0, MIN_REASON_LENGTH - reportReason.trim().length);

  return (
    <>
      <div className="user-action-btns">
        <button
          className="user-action-btn user-action-btn--report"
          onClick={() => setActiveModal("report")}
          title={`Report ${userName}`}
          aria-label={`Report ${userName}`}
        >
          <Flag size={15} />
        </button>

        <button
          className="user-action-btn user-action-btn--block"
          onClick={() => setActiveModal("block")}
          title={`Block ${userName}`}
          aria-label={`Block ${userName}`}
        >
          <Ban size={15} />
        </button>
      </div>

      {/* Block confirmation — rendered via portal to escape any parent transform/stacking context */}
      {createPortal(
        <ConfirmationPopup
          isOpen={activeModal === "block"}
          title="Block this user?"
          message={`You're about to block ${userName}. Their listings, profile, and messages will be hidden from you. This only affects your device.`}
          onConfirm={handleBlockConfirm}
          onCancel={() => setActiveModal(null)}
          confirmText="Block User"
          cancelText="Cancel"
        />,
        document.body
      )}

      {/* Report modal — rendered via portal, with textarea + validation */}
      {activeModal === "report" && createPortal(
        <div className="confirmation-overlay" onClick={() => setActiveModal(null)}>
          <div className="confirmation-card report-modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirmation-title">Report {userName}</h2>
            <p className="confirmation-message">
              Tell us what's wrong. Our team will review this account.
            </p>

            <div className="report-reason-field">
              <textarea
                className={`report-reason-textarea ${reasonError ? "report-reason-textarea--error" : ""}`}
                placeholder="Describe the issue (e.g. spam, fake listings, harassment…)"
                value={reportReason}
                onChange={handleReasonChange}
                rows={4}
                autoFocus
              />
              <div className="report-reason-meta">
                {reasonError ? (
                  <span className="report-reason-error">{reasonError}</span>
                ) : (
                  <span className="report-reason-hint">
                    {charsLeft > 0 ? `${charsLeft} more characters needed` : "Looks good!"}
                  </span>
                )}
                <span className="report-reason-count">{reportReason.trim().length}</span>
              </div>
            </div>

            <div className="confirmation-btn-row">
              <button className="confirmation-btn-cancel" onClick={() => setActiveModal(null)}>
                Cancel
              </button>
              <button
                className="confirmation-btn-confirm"
                onClick={handleReportSubmit}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default BlockUserButton;
